(() => {
  'use strict';

  const FREE_SHIPPING_THRESHOLD = 7500;
  let requestToken = 0;
  let recommendationToken = 0;
  let refreshTimer = 0;
  const recommendationCache = window.__lyCartRecommendationCache || (window.__lyCartRecommendationCache = new Map());

  const money = (cents, decimals = 0) => {
    try {
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        maximumFractionDigits: decimals,
        minimumFractionDigits: decimals
      }).format(Number(cents || 0) / 100);
    } catch (_) {
      return 'A$' + (Number(cents || 0) / 100).toFixed(decimals);
    }
  };

  const summaryTargets = () => {
    if (location.pathname === '/cart') {
      const page = document.querySelector('main .cart-summary,main .cart__summary,main form[action="/cart"]');
      return page ? [page] : [];
    }
    const drawer = document.querySelector('cart-drawer-component .cart-drawer__summary');
    return drawer ? [drawer] : [];
  };

  const renderShipping = (cart) => {
    const currency = String(cart?.currency || 'AUD').toUpperCase();
    const total = Number(cart?.total_price || 0);
    const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - total);
    const percent = Math.min(100, Math.round((total / FREE_SHIPPING_THRESHOLD) * 100));

    summaryTargets().forEach((target) => {
      let panel = target.querySelector(':scope > .lyastone-shipping-progress');
      if (!panel) {
        panel = document.createElement('section');
        panel.className = 'lyastone-shipping-progress';
        panel.setAttribute('aria-live', 'polite');
        panel.innerHTML = '<strong></strong><p></p><div class="lyastone-shipping-progress__track"><span></span></div><small></small>';
        target.prepend(panel);
      }

      const strong = panel.querySelector('strong');
      const copy = panel.querySelector('p');
      const track = panel.querySelector('.lyastone-shipping-progress__track');
      const small = panel.querySelector('small');

      if (currency !== 'AUD') {
        strong.textContent = 'Free AU/NZ standard shipping on orders A$75+';
        copy.textContent = 'The threshold is converted automatically at checkout.';
        track.hidden = true;
      } else {
        strong.textContent = remaining > 0
          ? "You're " + money(remaining) + ' away from free shipping'
          : 'Free AU & NZ shipping unlocked';
        copy.textContent = remaining > 0
          ? 'Add another piece to qualify.'
          : 'Free standard shipping applies to AU/NZ delivery addresses.';
        track.hidden = false;
        track.querySelector('span').style.width = percent + '%';
      }
      small.textContent = 'Sydney dispatch · 1–3 business days';
    });
  };

  const createRecommendationCard = (product) => {
    const card = document.createElement('article');
    card.className = 'ly-cart-rec-card';

    const media = document.createElement('a');
    media.className = 'ly-cart-rec-card__media';
    media.href = product.url || ('/products/' + product.handle);
    media.setAttribute('aria-label', product.title || 'View product');

    const image = document.createElement('img');
    const featured = typeof product.featured_image === 'string'
      ? product.featured_image
      : product.featured_image?.url;
    image.src = featured || product.images?.[0] || '';
    image.alt = product.title || '';
    image.loading = 'eager';
    image.decoding = 'async';
    image.width = 320;
    image.height = 400;
    media.append(image);

    const info = document.createElement('div');
    info.className = 'ly-cart-rec-card__info';

    const title = document.createElement('h3');
    title.className = 'ly-cart-rec-card__title';
    const titleLink = document.createElement('a');
    titleLink.className = 'ly-cart-rec-card__link';
    titleLink.href = media.href;
    titleLink.textContent = product.title || '';
    title.append(titleLink);

    const price = document.createElement('p');
    price.className = 'ly-cart-rec-card__price';
    price.textContent = money(product.price, 2);

    info.append(title, price);
    card.append(media, info);
    return card;
  };

  const renderRecommendations = async (cart) => {
    const section = document.querySelector('[data-ly-cart-recommendations]');
    const list = section?.querySelector('[data-ly-cart-recommendations-list]');
    const firstItem = cart?.items?.[0];

    if (!section || !list || !firstItem?.product_id) return;

    const cartKey = [
      firstItem.product_id,
      ...(cart.items || []).map((item) => item.product_id)
    ].join(':');

    if (section.dataset.recommendationKey === cartKey && list.childElementCount) {
      section.hidden = false;
      section.removeAttribute('aria-busy');
      return;
    }

    const cachedProducts = recommendationCache.get(cartKey);
    if (cachedProducts) {
      const cachedFragment = document.createDocumentFragment();
      cachedProducts.forEach((product) => cachedFragment.append(createRecommendationCard(product)));
      list.replaceChildren(cachedFragment);
      section.dataset.recommendationKey = cartKey;
      section.hidden = cachedProducts.length === 0;
      section.removeAttribute('aria-busy');
      return;
    }

    const current = ++recommendationToken;
    section.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(
        '/recommendations/products.json?product_id=' +
          encodeURIComponent(firstItem.product_id) +
          '&limit=6&intent=related',
        { credentials: 'same-origin', headers: { Accept: 'application/json' } }
      );
      if (!response.ok || current !== recommendationToken) return;

      const data = await response.json();
      const inCart = new Set((cart.items || []).map((item) => Number(item.product_id)));
      const products = (data.products || [])
        .filter((product) => product.available && !inCart.has(Number(product.id)))
        .slice(0, 4);

      recommendationCache.set(cartKey, products);
      const fragment = document.createDocumentFragment();
      products.forEach((product) => fragment.append(createRecommendationCard(product)));
      list.replaceChildren(fragment);
      section.dataset.recommendationKey = cartKey;
      section.hidden = products.length === 0;
    } catch (_) {
      if (!list.childElementCount) section.hidden = true;
    } finally {
      if (current === recommendationToken) section.removeAttribute('aria-busy');
    }
  };

  const refresh = async (event) => {
    const eventCart = event?.detail?.resource;
    if (eventCart && typeof eventCart.total_price === 'number' && Array.isArray(eventCart.items)) {
      window.setTimeout(() => {
        renderShipping(eventCart);
        renderRecommendations(eventCart);
      }, 80);
      return;
    }

    const current = ++requestToken;
    try {
      const response = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok || current !== requestToken) return;
      const cart = await response.json();
      renderShipping(cart);
      renderRecommendations(cart);
    } catch (_) {}
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh, { once: true });
  } else {
    refresh();
  }

  const scheduleRefresh = (event, delay = 160) => {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(() => refresh(event), delay);
  };

  ['cart:update', 'cart:refresh', 'cart:add', 'discount:update'].forEach((name) => {
    document.addEventListener(name, (event) => scheduleRefresh(event));
  });
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target?.closest?.('[data-ly-cart-recommendations]')) return;
    scheduleRefresh(undefined, 120);
  });
})();
